import axios from 'axios'
import { shallowRef } from 'vue'

const POLL_MS = 2000
// The job may wait behind another paid check on its queue, then run two model calls (300 s at most).
const GIVE_UP_MS = 8 * 60 * 1000

/**
 * RED-317 §9 — the drawer's AI draft for one overview. "Koosta uus AI-ga" and
 * "Uuesti" queue a draft on the server; its token is polled until the
 * suggestion or the reason there is none comes back, in plain Estonian from
 * the server. The suggestion: the AI's data paragraphs, the whole suggested
 * text (`blocks`: those paragraphs placed into the current version, the rest
 * carried over and marked "uuenda"), the current version's block texts
 * (`fingerprints`, null without a version), and the statistics table and
 * quarter. It is never written anywhere by this: the admin takes it into the
 * draft (suggestionInsert.js), where autosave picks it up.
 */
export function useMarketOverviewAiDraft() {
  const state = shallowRef('idle') // idle | working | ready | failed
  const suggestion = shallowRef(null) // { paragraphs, blocks, fingerprints, report, period, model }
  const error = shallowRef(null) // a message to show, or true for the generic one

  let ticket = 0
  let timer = null
  let startedAt = 0

  const stop = () => {
    clearTimeout(timer)
    timer = null
  }

  const reset = () => {
    ticket++
    stop()
    state.value = 'idle'
    suggestion.value = null
    error.value = null
  }

  const fail = message => {
    state.value = 'failed'
    error.value = message || true
  }

  const handle = (overviewId, data, mine) => {
    if (mine !== ticket) {
      return
    }
    if (data?.status === 'ready' && Array.isArray(data.paragraphs) && data.paragraphs.length) {
      suggestion.value = {
        paragraphs: data.paragraphs,
        blocks: Array.isArray(data.blocks) ? data.blocks : data.paragraphs.map(text => ({ kind: 'ai', text, style: null })),
        fingerprints: Array.isArray(data.fingerprints) ? data.fingerprints : null,
        report: data.report,
        period: data.period,
        model: data.model,
      }
      state.value = 'ready'
      error.value = null
    } else if (data?.status === 'queued' || data?.status === 'running') {
      poll(overviewId, data.token, mine)
    } else {
      fail(data?.message)
    }
  }

  const poll = (overviewId, token, mine) => {
    stop()
    timer = setTimeout(async () => {
      if (mine !== ticket) {
        return
      }
      if (Date.now() - startedAt > GIVE_UP_MS) {
        fail(null)
        return
      }
      try {
        const { data } = await axios.get(route('admin.market-overview.ai-draft.show', { marketOverview: overviewId, token }))
        handle(overviewId, data, mine)
      } catch {
        if (mine === ticket) {
          fail(null)
        }
      }
    }, POLL_MS)
  }

  /** Ask for a draft of `period` ("III kv 2026"); a previous suggestion stays shown until the new one replaces it. */
  const draft = async (overviewId, period) => {
    const mine = ++ticket
    stop()
    state.value = 'working'
    error.value = null
    startedAt = Date.now()
    try {
      const { data } = await axios.post(route('admin.market-overview.ai-draft.store', overviewId), { period: period ?? '' })
      handle(overviewId, data, mine)
    } catch (failure) {
      if (mine !== ticket) {
        return
      }
      const response = failure.response
      const errors = response?.data?.errors ?? {}
      const first = errors.ai ?? errors.period
      fail(response?.status === 422 && first ? (Array.isArray(first) ? first[0] : first) : response?.status === 429 ? response.data?.message : null)
    }
  }

  return { state, suggestion, error, draft, reset }
}
